// =============================================================================
// Admin Investors Management Page
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import { adminService, InvestorWithProfiles } from '../../services/admin.service';
import { getApiError, formatPhoneInput, formatPhoneDisplay, stripPhoneFormat } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Create Investor Modal
// =============================================================================

interface CreateInvestorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateInvestorModal({ isOpen, onClose, onCreated }: CreateInvestorModalProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const phoneDigits = stripPhoneFormat(phone);
      await adminService.createInvestor({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phoneDigits || undefined,
        sendWelcomeEmail,
      });
      await onCreated();
      onClose();
      // Reset form
      setEmail('');
      setFirstName('');
      setLastName('');
      setPhone('');
      setSendWelcomeEmail(true);
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add New Investor</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="investor@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="John"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Smith"
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="555-123-4567"
            />
          </div>

          {/* Welcome Email Toggle */}
          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-start gap-3">
              <div className="flex items-center h-5 mt-0.5">
                <input
                  id="sendWelcomeEmail"
                  type="checkbox"
                  checked={sendWelcomeEmail}
                  onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="sendWelcomeEmail" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Send welcome email
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  {sendWelcomeEmail
                    ? 'The investor will receive an email with instructions to activate their account.'
                    : 'No email will be sent. The investor can activate their account by visiting the portal and clicking "Activate your account".'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Investor'}
            </Button>
          </div>
        </form>

      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function InvestorsPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('investors', 'create');
  const [isLoading, setIsLoading] = useState(true);
  const [investors, setInvestors] = useState<InvestorWithProfiles[]>([]);
  const [filteredInvestors, setFilteredInvestors] = useState<InvestorWithProfiles[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadInvestors();
  }, []);

  useEffect(() => {
    filterInvestors();
  }, [investors, searchQuery]);

  const loadInvestors = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminService.listInvestors({ limit: 100 });
      setInvestors(result.investors);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load investors:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const filterInvestors = () => {
    if (!searchQuery) {
      setFilteredInvestors(investors);
      return;
    }

    const query = searchQuery.toLowerCase();
    setFilteredInvestors(
      investors.filter(
        (inv) =>
          inv.name.toLowerCase().includes(query) ||
          inv.email.toLowerCase().includes(query) ||
          inv.profiles.some(
            (p) =>
              p.legalName?.toLowerCase().includes(query) ||
              p.entityName?.toLowerCase().includes(query)
          )
      )
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'Never';
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getTotalInvested = (profiles: InvestorWithProfiles['profiles']) => {
    return profiles.reduce((sum, p) => sum + p.totalInvested, 0);
  };

  const getInvestmentCount = (profiles: InvestorWithProfiles['profiles']) => {
    return profiles.reduce((sum, p) => sum + p.investmentCount, 0);
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('investors', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Investors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage investor accounts and profiles
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-3">
            <Link to="/admin/investors/import">
              <Button
                variant="secondary"
                className="flex items-center gap-2"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                Import
              </Button>
            </Link>
            <Button
              className="flex items-center gap-2"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon className="h-4 w-4" />
              Add Investor
            </Button>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center gap-3 text-red-700">
            <span className="text-sm">Error: {error}</span>
            <button
              onClick={loadInvestors}
              className="text-sm font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Search & Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or entity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Investors</p>
          <p className="text-2xl font-bold text-gray-900">{total}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Invested</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(investors.reduce((sum, inv) => sum + getTotalInvested(inv.profiles), 0))}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Active This Week</p>
          <p className="text-2xl font-bold text-gray-900">
            {
              investors.filter(
                (inv) =>
                  inv.lastLoginAt &&
                  Date.now() - new Date(inv.lastLoginAt).getTime() < 7 * 24 * 60 * 60 * 1000
              ).length
            }
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Authenticator Enabled</p>
          <p className="text-2xl font-bold text-gray-900">
            {investors.filter((inv) => inv.totpEnabled).length}
          </p>
        </Card>
      </div>

      {/* Investors List */}
      <div className="space-y-4">
        {filteredInvestors.map((investor) => (
          <Card key={investor.id} padding="md" hover>
            <Link to={`/admin/investors/${investor.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Investor Info */}
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex h-12 w-12 rounded-full bg-primary-100 items-center justify-center">
                    <span className="text-lg font-semibold text-primary-700">
                      {investor.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {investor.name}
                      </h3>
                      {investor.totpEnabled && (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                          Auth
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <EnvelopeIcon className="h-4 w-4" />
                      {investor.email}
                    </div>
                    {investor.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <PhoneIcon className="h-4 w-4" />
                        {formatPhoneDisplay(investor.phone)}
                      </div>
                    )}
                    {/* Profiles */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {investor.profiles.map((profile) => (
                        <span
                          key={profile.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                        >
                          {profile.type === 'INDIVIDUAL' ? (
                            <UserCircleIcon className="h-3 w-3" />
                          ) : (
                            <BuildingOfficeIcon className="h-3 w-3" />
                          )}
                          {profile.entityName || profile.legalName || 'Unnamed Profile'}
                        </span>
                      ))}
                      {investor.profiles.length === 0 && (
                        <span className="text-xs text-gray-400 italic">No profiles yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <p className="text-xs text-gray-500">Investments</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {getInvestmentCount(investor.profiles)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Invested</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(getTotalInvested(investor.profiles))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Last Login</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatTimeAgo(investor.lastLoginAt)}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredInvestors.length === 0 && !error && (
        <Card padding="lg">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Investors Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery
                ? 'Try adjusting your search'
                : 'Add your first investor to get started'}
            </p>
            {!searchQuery && canCreate && (
              <Button
                className="mt-4"
                onClick={() => setShowCreateModal(true)}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Investor
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Create Investor Modal */}
      <CreateInvestorModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={loadInvestors}
      />
    </div>
  );
}

export default InvestorsPage;
